// Market Watch Excel Helper Functions
import { MW_LAYOUT, SHEET_PREFIXES } from "../../../shared/constants";

// Shared timestamp generator — call once per run so all sheets share the same timestamp
export const generateTimestamp = (): string => {
  const now = new Date();
  let hours = now.getHours();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  const h = String(hours).padStart(2, "0");
  const m = String(now.getMinutes()).padStart(2, "0");
  const s = String(now.getSeconds()).padStart(2, "0");
  return `${h}-${m}-${s} ${ampm}`;
};

import {
  DynamicKPI,
  DynamicChart,
  FilterContext,
  EnrichedData,
  CalculatedColumn,
  PivotConfig,
} from "./types";
import { THEME, STRATEGY_HIGHLIGHT } from "./constants";
import { parseHtmlIntoSectionsDetailed, sanitizeRow, HtmlBadgeType } from "./utils";
import { calculateRowHeight } from "./excelStyleTypes";
import { generateDynamicKPIs } from "./kpiGenerator";
import { generateDynamicCharts } from "./chartGenerator";
import type { PerAsinAnalysis } from "../../../shared/apiService";

/**
 * Display-label overrides for Market Watch pivot value fields.
 * Excel auto-generates labels like "Sum of asin_purchase_volume_forecast" from
 * (aggregation, sourceField). These overrides replace the visible label only —
 * the underlying source column names in the hidden _MMData_ sheet are unchanged.
 *
 * Key format: "<sourceField>:<aggregation>" — matches the keys produced from
 * `pivotConfig.values`. Aggregation strings come from the backend
 * MARKET_WATCH_PIVOT_CONFIG ("sum", "count").
 */
export const MW_PIVOT_VALUE_LABEL_OVERRIDES: Record<string, string> = {
  "asin_purchase_volume_forecast:sum": "L7 Unit Sales For Target ASIN",
  "adjusted_purchase_vol_forecast:sum": "L7 Unit Sales For All ASINs",
  "asin:count": "Number of Total ASINs",
};

/**
 * Display-label overrides for the flattened MW Recommendations sheet
 * (`createFlattenedGridSheet`). The grouped_grid data dictionary uses raw
 * Snowflake column keys (`asin_purchase_volume_forecast`, ...), which are
 * load-bearing for downstream lookups (`ROUND_COLUMNS`, `headers.indexOf(...)`,
 * strategy_rank row coloring, backend classifier tags). We only override the
 * visible header text — never the underlying keys.
 *
 * Mirrors `MW_PIVOT_VALUE_LABEL_OVERRIDES` so the two sheets show the same
 * merchant-facing labels. Update both maps together when a column is renamed.
 */
export const MW_FLATTENED_GRID_HEADER_OVERRIDES: Record<string, string> = {
  asin_purchase_volume_forecast: "L7 Unit Sales For Target ASIN",
  adjusted_purchase_vol_forecast: "L7 Unit Sales For All ASINs",
};

/**
 * Returns a new array of display labels for the flattened-grid header row.
 * Any key not in the override map is returned unchanged. Pure function: does
 * not mutate the input and does not depend on Excel context.
 */
export const applyFlattenedGridHeaderOverrides = (headers: string[]): string[] =>
  headers.map((h) => MW_FLATTENED_GRID_HEADER_OVERRIDES[h] ?? h);

/**
 * Return a copy of `rows` with two field positions swapped, if BOTH fields are
 * present. If either is missing, return a copy unchanged. Never mutates input.
 */
export const swapAdjacentRowFields = (rows: string[], fieldA: string, fieldB: string): string[] => {
  const result = [...rows];
  const idxA = result.indexOf(fieldA);
  const idxB = result.indexOf(fieldB);
  if (idxA < 0 || idxB < 0) return result;
  [result[idxA], result[idxB]] = [result[idxB], result[idxA]];
  return result;
};

/**
 * Build the row-field order for the "MW Pivot By Feature" pivot:
 * places `global_cluster_feature_label` before `global_price_cluster_label`,
 * preserving every other position.
 */
export const buildFeatureFirstRowOrder = (rows: string[]): string[] =>
  swapAdjacentRowFields(rows, "global_price_cluster_label", "global_cluster_feature_label");

/**
 * Extract lower and upper price bounds from a price range string.
 * E.g., "$0.16-$40.29" → [0.16, 40.29]
 * Returns [Infinity, Infinity] for unparseable/missing values.
 */
export const extractPriceBounds = (label: string | null | undefined): [number, number] => {
  if (!label) return [Infinity, Infinity];
  const pattern = /\$?([\d.]+)/g;
  const firstMatch = pattern.exec(label);
  const secondMatch = pattern.exec(label);
  const lower = firstMatch ? parseFloat(firstMatch[1]) : Infinity;
  const upper = secondMatch ? parseFloat(secondMatch[1]) : Infinity;
  return [lower, upper];
};

/**
 * Sort grouped_grid price cluster blocks within each ASIN group
 * by numeric lower bound of the price cluster label.
 * Preserves the level 0→1→2→3→4 hierarchy.
 */
const sortGroupedGridByPriceCluster = (
  grid: Array<Record<string, any>>,
  priceClusterKey: string
): Array<Record<string, any>> => {
  if (!grid || grid.length === 0) return grid;

  const result: Array<Record<string, any>> = [];
  let i = 0;

  while (i < grid.length) {
    const row = grid[i];
    const level = Number(row._level ?? 0);

    if (level <= 1) {
      result.push(row);
      i++;

      // If level 1, collect all its level 2+ children into blocks
      if (level === 1) {
        const blocks: Array<Array<Record<string, any>>> = [];
        let currentBlock: Array<Record<string, any>> = [];

        while (i < grid.length && Number(grid[i]._level ?? 0) >= 2) {
          if (Number(grid[i]._level ?? 0) === 2) {
            if (currentBlock.length > 0) blocks.push(currentBlock);
            currentBlock = [grid[i]];
          } else {
            currentBlock.push(grid[i]);
          }
          i++;
        }
        if (currentBlock.length > 0) blocks.push(currentBlock);

        // Sort blocks by price cluster lower bound
        blocks.sort((a, b) => {
          const boundsA = extractPriceBounds(a[0]?.[priceClusterKey]);
          const boundsB = extractPriceBounds(b[0]?.[priceClusterKey]);
          return (boundsA[0] - boundsB[0]) || (boundsA[1] - boundsB[1]);
        });

        for (const block of blocks) {
          result.push(...block);
        }
      }
    } else {
      result.push(row);
      i++;
    }
  }

  return result;
};

/**
 * Sort Level 3 sub-blocks by `priority_score` descending within each Level 2
 * group. A "sub-block" is a Level 3 row followed by its child Level 4 rows.
 * Level 0/1/2 positions are untouched. Pure function — does not mutate input.
 */
export const sortBlocksByPriorityScore = (
  grid: Array<Record<string, any>>
): Array<Record<string, any>> => {
  if (!grid || grid.length === 0) return [];

  const result: Array<Record<string, any>> = [];
  let i = 0;

  while (i < grid.length) {
    const level = Number(grid[i]._level ?? 0);

    if (level !== 2) {
      result.push(grid[i]);
      i++;
      continue;
    }

    // Push the Level 2 row itself
    result.push(grid[i]);
    i++;

    // Collect Level 3 sub-blocks within this Level 2 group
    const subBlocks: Array<Array<Record<string, any>>> = [];
    let currentBlock: Array<Record<string, any>> = [];

    while (i < grid.length && Number(grid[i]._level ?? 0) >= 3) {
      if (Number(grid[i]._level ?? 0) === 3) {
        if (currentBlock.length > 0) subBlocks.push(currentBlock);
        currentBlock = [grid[i]];
      } else {
        currentBlock.push(grid[i]);
      }
      i++;
    }
    if (currentBlock.length > 0) subBlocks.push(currentBlock);

    // Sort sub-blocks by the Level 3 row's priority_score descending
    subBlocks.sort((a, b) => {
      const scoreA = Number(a[0]?.priority_score ?? 0);
      const scoreB = Number(b[0]?.priority_score ?? 0);
      return scoreB - scoreA;
    });

    for (const block of subBlocks) {
      result.push(...block);
    }
  }

  return result;
};

/**
 * Format a date range label from start/end date strings (e.g. "2026-03-01").
 * Returns e.g. "Data period: Mar 1, 2026 – Mar 7, 2026" or fallback if dates are missing.
 */
export const formatDateRangeLabel = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return "Based on last week's data";
  try {
    const fmt = (d: string) =>
      new Date(d + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    return `Data period: ${fmt(startDate)} – ${fmt(endDate)}`;
  } catch {
    return "Based on last week's data";
  }
};

// Helper function to write calculated column descriptions above data
// Returns the row number where data should start (after descriptions + empty row)
export const writeCalculatedColumnsDescription = (
  sheet: Excel.Worksheet,
  startRow: number,
  calculatedColumns: CalculatedColumn[] | undefined
): number => {
  if (!calculatedColumns || calculatedColumns.length === 0) {
    console.log("writeCalculatedColumnsDescription: No calculated columns to display");
    return startRow;
  }

  console.log("writeCalculatedColumnsDescription: Writing", calculatedColumns.length, "calculated columns");

  for (let i = 0; i < calculatedColumns.length; i++) {
    const col = calculatedColumns[i];
    const cell = sheet.getRangeByIndexes(startRow + i, 0, 1, 1);
    // Include formula if available for transparency
    const formulaText = col.formula ? ` | Formula: ${col.formula}` : "";
    const displayText = `${col.name}: ${col.description}${formulaText}`;
    console.log(`  - ${col.name}: formula=${col.formula || "(none)"}`);
    cell.values = [[displayText]];
    cell.format.font.italic = true;
    cell.format.font.color = "#475569";
  }

  // Return row after descriptions + 1 empty row
  return startRow + calculatedColumns.length + 1;
};

// Helper: Create data sheet with raw filtered data
export const createDataSheet = async (
  context: Excel.RequestContext,
  data: { headers: string[]; rows: any[][] },
  appliedSummary: string,
  timestamp: string,
  preservedColumns?: { headers: string[]; data: any[][] },
  datePeriod?: { startDate?: string; endDate?: string }
) => {
  // Merge API headers/data with any preserved user-added columns
  const mergedHeaders = preservedColumns
    ? [...data.headers, ...preservedColumns.headers]
    : data.headers;
  const mergedRows = preservedColumns
    ? data.rows.map((row, i) => {
        const pRow = preservedColumns.data[i];
        return [...row, ...(pRow ?? new Array(preservedColumns.headers.length).fill(""))];
      })
    : data.rows;

  const sheetName = `${SHEET_PREFIXES.MW_RAW_DATA} ${timestamp}`;
  const sheet = context.workbook.worksheets.add(sheetName);
  await context.sync();

  // Row 1-2: Title (merged A1:K2)
  const titleRange = sheet.getRange("A1:K2");
  titleRange.merge();
  sheet.getRange("A1").values = [["Market Watch Data Overview"]];
  titleRange.format.font.size = 16;
  titleRange.format.font.bold = true;
  titleRange.format.font.color = "#1F4788";
  titleRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  titleRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  titleRange.format.fill.color = "#F0F4F8";

  // Row 3: Applied filters (merged A3:K3)
  if (appliedSummary) {
    const filterRange = sheet.getRange("A3:K3");
    filterRange.merge();
    sheet.getRange("A3").values = [[appliedSummary]];
    filterRange.format.font.size = 10;
    filterRange.format.font.italic = true;
    filterRange.format.font.color = "#666666";
    filterRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    filterRange.format.verticalAlignment = Excel.VerticalAlignment.top;
    filterRange.format.fill.color = "#F0F4F8";
    filterRange.format.wrapText = true;
    filterRange.format.rowHeight = calculateRowHeight(appliedSummary, 700);
  }

  // Row 4: Data period label
  const periodLabel = formatDateRangeLabel(datePeriod?.startDate, datePeriod?.endDate);
  const periodRange = sheet.getRange("A4:K4");
  periodRange.merge();
  sheet.getRange("A4").values = [[periodLabel]];
  periodRange.format.font.size = 10;
  periodRange.format.font.italic = true;
  periodRange.format.font.color = "#6B7280";
  periodRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  periodRange.format.fill.color = "#F0F4F8";
  sheet.getRange("A4").format.rowHeight = 20;

  // Row 6: Column headers
  if (mergedHeaders.length > 0) {
    const headerRange = sheet.getRangeByIndexes(MW_LAYOUT.HEADER_ROW, 0, 1, mergedHeaders.length);
    headerRange.values = [mergedHeaders.map((h) => h ?? "")];
    headerRange.format.font.bold = true;
  }

  // Row 7+: Data rows (sanitize values for Excel)
  if (mergedRows.length > 0) {
    const sanitizedRows = mergedRows.map((row) =>
      row.map((cell: any) => {
        if (cell === null || cell === undefined) return "";
        if (typeof cell === "object") return JSON.stringify(cell);
        return cell;
      })
    );
    const dataRange = sheet.getRangeByIndexes(MW_LAYOUT.DATA_START_ROW, 0, mergedRows.length, mergedHeaders.length);
    dataRange.values = sanitizedRows;
  }

  // Create Excel Table for filtering, sorting, and banded rows
  if (mergedHeaders.length > 0 && mergedRows.length > 0) {
    const tableRange = sheet.getRangeByIndexes(MW_LAYOUT.HEADER_ROW, 0, mergedRows.length + 1, mergedHeaders.length);
    const table = sheet.tables.add(tableRange, true);
    table.name = `MWData_${Date.now()}`;
    table.style = "TableStyleMedium2";

    // Pre-sort by target_asin ASC, search_term ASC, overall_position ASC
    const sortColumns = ["target_asin", "search_term", "overall_position"];
    const sortFields: Excel.SortField[] = sortColumns
      .map((col) => mergedHeaders.findIndex((h) => h === col))
      .filter((idx) => idx !== -1)
      .map((key) => ({ key, ascending: true }));

    if (sortFields.length > 0) {
      table.sort.apply(sortFields);
    }
  }

  sheet.getUsedRange().format.autofitColumns();
  await context.sync();

  return sheet;
};

// Read user-added/calculated columns from an existing MW Raw Data sheet so they
// can be carried over during a Preserve operation.  Returns only columns whose
// headers are NOT already present in the new API data headers.
export const readPreservedColumns = async (
  context: Excel.RequestContext,
  oldSheet: Excel.Worksheet,
  newApiHeaders: string[]
): Promise<{ headers: string[]; data: any[][] }> => {
  const empty = { headers: [], data: [] };

  // Row 6 (0-indexed 5) contains headers in MW Raw Data sheets
  const usedRange = oldSheet.getUsedRange();
  usedRange.load(["rowCount", "columnCount"]);
  await context.sync();

  if (usedRange.rowCount < MW_LAYOUT.MIN_ROWS_FOR_HEADER) return empty; // no header row yet

  const headerRange = oldSheet.getRangeByIndexes(MW_LAYOUT.HEADER_ROW, 0, 1, usedRange.columnCount);
  headerRange.load("values");
  await context.sync();

  const oldHeaders: string[] = (headerRange.values[0] ?? []).map((h: any) =>
    h != null ? String(h) : ""
  );

  // Identify columns NOT in the new API headers
  const newHeaderSet = new Set(newApiHeaders);
  const preserveIndices: number[] = [];
  const preserveHeaders: string[] = [];
  oldHeaders.forEach((h, i) => {
    if (h && !newHeaderSet.has(h)) {
      preserveIndices.push(i);
      preserveHeaders.push(h);
    }
  });

  if (preserveIndices.length === 0) return empty;

  // Read data rows (row 7+, 0-indexed DATA_START_ROW+)
  const dataRowCount = usedRange.rowCount - MW_LAYOUT.DATA_START_ROW;
  if (dataRowCount <= 0) return { headers: preserveHeaders, data: [] };

  const dataRange = oldSheet.getRangeByIndexes(MW_LAYOUT.DATA_START_ROW, 0, dataRowCount, usedRange.columnCount);
  dataRange.load("values");
  await context.sync();

  const preservedData: any[][] = dataRange.values.map((row: any[]) =>
    preserveIndices.map((colIdx) => {
      const val = row?.[colIdx];
      return val !== undefined && val !== null ? val : "";
    })
  );

  return { headers: preserveHeaders, data: preservedData };
};

// Helper: Create the hidden _MMData_* source sheet that pivot tables read from.
// Extracted so multiple pivots (e.g. MW Pivot By Price + MW Pivot By Feature)
// can SHARE one source sheet — the dataset is written to Excel exactly once,
// which is the single largest cost in the sheet-creation phase for large
// Market Watch datasets.
export const createPivotSourceSheet = async (
  context: Excel.RequestContext,
  enrichedTable: { headers: string[]; rows: any[][] }
): Promise<Excel.Table | null> => {
  const workbook = context.workbook;
  const headers = enrichedTable?.headers || [];
  const rawData = enrichedTable?.rows || [];

  if (headers.length === 0) {
    console.warn("No headers for pivot source sheet, skipping");
    return null;
  }

  // Sanitize data for Excel
  const data = rawData.map(sanitizeRow);

  console.log("Creating pivot source sheet:", { headers: headers.length, rows: data.length });

  const dataSheetName = `_MMData_${Date.now()}`;
  const dataSheet = workbook.worksheets.add(dataSheetName);

  // Write source data to hidden sheet (the dominant cost — N rows × C cols cells)
  const headerRange = dataSheet.getRangeByIndexes(0, 0, 1, headers.length);
  headerRange.values = [headers];

  if (data.length > 0) {
    const dataRange = dataSheet.getRangeByIndexes(1, 0, data.length, headers.length);
    dataRange.values = data;
  }

  const fullRange = dataSheet.getRangeByIndexes(0, 0, data.length + 1, headers.length);
  const sourceTable = dataSheet.tables.add(fullRange, true);
  sourceTable.name = `MMSource_${Date.now()}`;

  // Hide the data sheet — it's an implementation detail, not user-facing
  dataSheet.visibility = Excel.SheetVisibility.hidden;

  await context.sync();

  return sourceTable;
};

// Helper: Create pivot table sheet from analysis result.
//
// `sheetName`         — explicit pivot sheet name (e.g. "MW Pivot By Price 02-15-00 pm").
//                       Caller is responsible for length-budget compliance (≤ 31 chars).
// `sourceTable`       — pre-built source Excel.Table (from createPivotSourceSheet).
//                       Passing a shared sourceTable across multiple pivot calls
//                       avoids re-writing the dataset to Excel for each pivot.
// `rowFieldsOverride` — optional explicit row hierarchy order. If omitted, falls back
//                       to `pivotConfig.rows`. Used for the "MW Pivot By Feature" pivot,
//                       which permutes price/feature without altering pivotConfig itself.
export const createPivotSheet = async (
  context: Excel.RequestContext,
  enrichedTable: { headers: string[]; rows: any[][] },
  pivotConfig: PivotConfig,
  _timestamp: string,
  sheetName: string,
  sourceTable: Excel.Table,
  rowFieldsOverride?: string[]
) => {
  const workbook = context.workbook;
  const headers = enrichedTable?.headers || [];

  if (headers.length === 0 || !sourceTable) {
    console.warn("Cannot create pivot sheet — missing headers or sourceTable", { sheetName });
    return null;
  }

  console.log("Creating pivot sheet:", { sheetName, headers: headers.length });

  // Create pivot sheet (visible)
  const pivotSheet = workbook.worksheets.add(sheetName);

  // Add title
  const titleRange = pivotSheet.getRange("A1:F1");
  titleRange.merge();
  pivotSheet.getRange("A1").values = [["Market Watch Pivot Analysis"]];
  titleRange.format.font.size = 14;
  titleRange.format.font.bold = true;
  titleRange.format.font.color = "#1F4788";
  titleRange.format.fill.color = "#F0F4F8";

  // Write calculated column descriptions above pivot table (starting at row 2, index 1)
  const pivotStartRow = writeCalculatedColumnsDescription(
    pivotSheet,
    1,
    pivotConfig.calculated_columns
  );

  await context.sync();

  // Convert row number to Excel cell reference (e.g., 1 -> "A2", 3 -> "A4")
  const pivotStartCell = `A${pivotStartRow + 1}`;

  const pivotTable = pivotSheet.pivotTables.add(
    `MMPivot_${Date.now()}`,
    sourceTable.getRange(),
    pivotSheet.getRange(pivotStartCell)
  );

  await context.sync();

  const { rows: configRows, columns, values } = pivotConfig || {};
  const rowFields = rowFieldsOverride ?? configRows ?? [];

  // Add row hierarchies in the resolved order (override wins over pivotConfig.rows)
  for (const rowField of rowFields) {
    if (headers.includes(rowField)) {
      try {
        pivotTable.rowHierarchies.add(pivotTable.hierarchies.getItem(rowField));
      } catch (e) {
        console.warn(`Could not add row hierarchy: ${rowField}`, e);
      }
    }
  }

  // Add column hierarchies
  for (const colField of columns || []) {
    if (headers.includes(colField)) {
      try {
        pivotTable.columnHierarchies.add(pivotTable.hierarchies.getItem(colField));
      } catch (e) {
        console.warn(`Could not add column hierarchy: ${colField}`, e);
      }
    }
  }

  // Add value hierarchies
  const aggregationMap: Record<string, Excel.AggregationFunction> = {
    sum: Excel.AggregationFunction.sum,
    average: Excel.AggregationFunction.average,
    count: Excel.AggregationFunction.count,
    min: Excel.AggregationFunction.min,
    max: Excel.AggregationFunction.max,
  };

  // Add value hierarchies WITHOUT renaming yet — the rename invalidates the
  // captured DataPivotHierarchy proxy for `sortByValues`, which fails with
  // "RichApi.Error: The requested resource doesn't exist." We capture the
  // adjusted-volume reference here, run the sort first, THEN apply renames.
  const valueHierarchyRenames: { hierarchy: Excel.DataPivotHierarchy; label: string }[] = [];
  let adjustedVolumeDataHierarchy: Excel.DataPivotHierarchy | null = null;

  for (const valueConfig of values || []) {
    if (headers.includes(valueConfig.field)) {
      try {
        const dataHierarchy = pivotTable.dataHierarchies.add(
          pivotTable.hierarchies.getItem(valueConfig.field)
        );
        if (valueConfig.aggregation && aggregationMap[valueConfig.aggregation]) {
          dataHierarchy.summarizeBy = aggregationMap[valueConfig.aggregation];
        }
        // Format forecast values as whole numbers (no decimals)
        dataHierarchy.numberFormat = "#,##0";

        // Stash the rename for AFTER sort — applying it now invalidates the
        // proxy for sortByValues's resource resolution.
        const overrideKey = `${valueConfig.field}:${valueConfig.aggregation}`;
        const overrideLabel = MW_PIVOT_VALUE_LABEL_OVERRIDES[overrideKey];
        if (overrideLabel) {
          valueHierarchyRenames.push({ hierarchy: dataHierarchy, label: overrideLabel });
        }

        // Capture the reference for sortByValues (used to sort every row level
        // by Sum of adjusted_purchase_vol_forecast descending).
        if (
          valueConfig.field === "adjusted_purchase_vol_forecast" &&
          valueConfig.aggregation === "sum"
        ) {
          adjustedVolumeDataHierarchy = dataHierarchy;
        }
      } catch (e) {
        console.warn(`Could not add value hierarchy: ${valueConfig.field}`, e);
      }
    }
  }

  await context.sync();

  // Phase 1 — Pre-sort EVERY row level by Sum of adjusted_purchase_vol_forecast
  // descending. Runs BEFORE the data-hierarchy rename so the captured proxy
  // resolves correctly. One batched sync — Excel recomputes the pivot once.
  if (adjustedVolumeDataHierarchy) {
    for (const rowField of rowFields) {
      if (!headers.includes(rowField)) continue;
      try {
        const hierarchy = pivotTable.rowHierarchies.getItem(rowField);
        const field = hierarchy.fields.getItem(rowField);
        field.sortByValues(Excel.SortBy.descending, adjustedVolumeDataHierarchy);
      } catch (queueErr) {
        console.warn(`Could not queue sort for row level "${rowField}":`, queueErr);
      }
    }
    try {
      await context.sync();
    } catch (sortSyncErr) {
      console.warn("Pre-sort sync failed (partial sort may apply):", sortSyncErr);
    }
  }

  // Phase 2 — Apply value-hierarchy display label overrides (now safe — sort
  // has already used the original-named proxy).
  for (const { hierarchy, label } of valueHierarchyRenames) {
    hierarchy.name = label;
  }

  // Phase 3 — Pre-collapse ONLY the level-1 field (the direct child of the
  // topmost row field). When the level-1 items are collapsed, every level
  // below is automatically hidden by Excel — collapsing the deeper levels
  // (search_term, title, asin) is wasted work (thousands of items × queued
  // Office.js `isExpanded = false` operations is the source of multi-minute
  // pivot build times). Excel's pivot semantics: collapsing a parent hides
  // every descendant regardless of THEIR isExpanded state. Drill-down still
  // works — when the user expands a level-1 item, the level-2 items show in
  // their default state and Excel re-renders that subtree quickly.
  if (rowFields.length >= 2) {
    const collapseFieldName = rowFields[1];
    if (headers.includes(collapseFieldName)) {
      try {
        const hierarchy = pivotTable.rowHierarchies.getItem(collapseFieldName);
        const field = hierarchy.fields.getItem(collapseFieldName);
        const items = field.items;
        items.load("items/isExpanded");
        await context.sync();

        for (const item of items.items) {
          item.isExpanded = false;
        }
        await context.sync();
      } catch (collapseError) {
        console.warn(
          `Could not pre-collapse level-1 row field "${collapseFieldName}":`,
          collapseError
        );
      }
    }
  }

  return pivotSheet;
};

const SEASONALITY_PREFIX_TO_STRIP = "Estimates adjusted for seasonality. ";

/**
 * Strip the JESS-sheet-specific prefix from a seasonality note so it reads
 * naturally as a header annotation. JESS uses "Estimates adjusted for
 * seasonality. Demand is rising..." which only makes sense above metric
 * cards. The header just wants "Demand is rising...".
 */
const stripSeasonalityPrefix = (note: string): string => {
  if (note.startsWith(SEASONALITY_PREFIX_TO_STRIP)) {
    return note.slice(SEASONALITY_PREFIX_TO_STRIP.length).trim();
  }
  return note;
};

/**
 * Write a seasonality annotation block immediately below the flattened-grid
 * title for every target ASIN with a material directional signal. Returns
 * the next available row index (where the caller should resume writing —
 * typically the calculated column descriptions).
 *
 * Discipline rule: ASINs with null/empty `seasonality_note` are skipped.
 * When no target ASIN has a material signal, this is a no-op and returns
 * startRow unchanged so the existing layout is preserved.
 *
 *   Block format per target ASIN:
 *     Row N   : "Target ASIN: B0XXXXX"             (bold)
 *     Row N+1 : "⚠ Seasonality: <stripped note>"   (italic, wrapText)
 *
 *   After all per-ASIN blocks, one empty separator row.
 */
export const writeSeasonalityHeaderBlock = (
  sheet: Excel.Worksheet,
  perAsinAnalysis: PerAsinAnalysis[] | undefined,
  startRow: number,
  columnCount: number
): number => {
  if (!perAsinAnalysis || perAsinAnalysis.length === 0) {
    return startRow;
  }

  const materialEntries = perAsinAnalysis.filter(
    (entry): entry is PerAsinAnalysis & { seasonality_note: string } =>
      typeof entry.seasonality_note === "string" && entry.seasonality_note.trim().length > 0
  );

  if (materialEntries.length === 0) {
    return startRow; // No material signal across any target ASIN — render nothing.
  }

  const lastCol = Math.max(columnCount - 1, 5); // ensure at least A:F merge width
  let rowIdx = startRow;

  for (const entry of materialEntries) {
    // ASIN identification row.
    const asinRange = sheet.getRangeByIndexes(rowIdx, 0, 1, lastCol + 1);
    asinRange.merge();
    sheet.getRangeByIndexes(rowIdx, 0, 1, 1).values = [[`Target ASIN: ${entry.target_asin}`]];
    asinRange.format.font.bold = true;
    asinRange.format.font.size = 12;
    asinRange.format.font.color = "#1F4788";
    asinRange.format.fill.color = "#F0F4F8";
    sheet.getRangeByIndexes(rowIdx, 0, 1, 1).format.rowHeight = 22;
    rowIdx += 1;

    // Seasonality note row.
    const noteRange = sheet.getRangeByIndexes(rowIdx, 0, 1, lastCol + 1);
    noteRange.merge();
    const noteText = `Seasonality: ${stripSeasonalityPrefix(entry.seasonality_note)}`;
    sheet.getRangeByIndexes(rowIdx, 0, 1, 1).values = [[noteText]];
    noteRange.format.font.italic = true;
    noteRange.format.font.size = 11;
    noteRange.format.font.color = "#8B5500";
    noteRange.format.fill.color = "#FFF7E6";
    noteRange.format.wrapText = true;
    noteRange.format.verticalAlignment = Excel.VerticalAlignment.center;
    sheet.getRangeByIndexes(rowIdx, 0, 1, 1).format.rowHeight = 28;
    rowIdx += 1;
  }

  // Empty separator row between the block and the existing title.
  rowIdx += 1;
  return rowIdx;
};

// Helper: Create flattened grid sheet with grouped/aggregated data
export const createFlattenedGridSheet = async (
  context: Excel.RequestContext,
  inputGroupedGrid: Array<Record<string, any>>,
  pivotConfig?: PivotConfig,
  timestamp?: string,
  perAsinAnalysis?: PerAsinAnalysis[]
) => {
  if (!inputGroupedGrid || inputGroupedGrid.length === 0) {
    console.warn("No grouped grid data for flattened sheet, skipping");
    return null;
  }

  const workbook = context.workbook;
  const ts = timestamp || generateTimestamp();
  const sheetName = `MW Rec Details ${ts}`;
  const sheet = workbook.worksheets.add(sheetName);

  // Get headers from the first row of grouped grid (excluding internal fields like _level)
  // Also exclude strategy_rank — it drives row color coding (read from groupedGrid JS array) but should not be user-facing
  const headers = Object.keys(inputGroupedGrid[0]).filter(
    (k) => !k.startsWith("_") && k !== "strategy_rank"
  );

  // Sort price cluster blocks within each ASIN group by numeric lower bound
  const priceClusterKey = headers.includes("dynamic_price_cluster_label")
    ? "dynamic_price_cluster_label"
    : headers.includes("global_price_cluster_label")
      ? "global_price_cluster_label"
      : null;

  const priceSorted = priceClusterKey
    ? sortGroupedGridByPriceCluster(inputGroupedGrid, priceClusterKey)
    : inputGroupedGrid;

  const groupedGrid = sortBlocksByPriorityScore(priceSorted);

  const ROUND_COLUMNS = new Set([
    "asin_purchase_volume_forecast",
    "adjusted_purchase_vol_forecast",
    "Demand Share %",
    "opportunity_units"
  ]);

  const data = groupedGrid.map((row) =>
    headers.map((h) => {
      const val = row[h] ?? "";
      if (h === "search_term" && val === "") {
        return "AGGREGATE";
      }
      if (ROUND_COLUMNS.has(h) && typeof val === "number") {
        return Math.round(val);
      }
      return val;
    })
  );

  console.log("Creating flattened grid sheet with:", {
    headers: headers.length,
    rows: data.length,
  });

  // Title at A1 (unchanged from pre-feature layout) — the heading always
  // sits at the top of the sheet so users see the report name first.
  const titleRange = sheet.getRange("A1:F1");
  titleRange.merge();
  sheet.getRange("A1").values = [["Market Watch Detailed Data Analysis"]];
  titleRange.format.font.size = 14;
  titleRange.format.font.bold = true;
  titleRange.format.font.color = "#1F4788";
  titleRange.format.fill.color = "#F0F4F8";

  // Write the seasonality header block BELOW the title (one block per target
  // ASIN with a material directional signal). Returns the next available row.
  // No-op when all target ASINs are stable — returns startRow unchanged so
  // the calculated column descriptions land at row 2 as before.
  const afterSeasonalityRow = writeSeasonalityHeaderBlock(
    sheet,
    perAsinAnalysis,
    /* startRow */ 1,
    /* columnCount */ headers.length
  );

  // Write calculated column descriptions immediately after the seasonality block.
  const dataStartRow = writeCalculatedColumnsDescription(
    sheet,
    afterSeasonalityRow,
    pivotConfig?.calculated_columns
  );

  // Write column headers — apply display overrides so the user-facing labels
  // match the MW Pivot sheet. `headers` (raw keys) is still used for every
  // downstream lookup (ROUND_COLUMNS, headers.indexOf, table column filters)
  // because the underlying grouped_grid data dictionary keys are unchanged.
  const displayHeaders = applyFlattenedGridHeaderOverrides(headers);
  const headerRange = sheet.getRangeByIndexes(dataStartRow, 0, 1, headers.length);
  headerRange.values = [displayHeaders];
  headerRange.format.font.bold = true;
  headerRange.format.fill.color = "#068E75";
  headerRange.format.font.color = "#FFFFFF";

  // Force text format on currency columns BEFORE writing values so Excel
  // doesn't auto-convert "$" to the system locale symbol (e.g. "₹" on Indian systems)
  const revenueIndex = headers.indexOf("opportunity_revenue");
  if (revenueIndex >= 0 && data.length > 0) {
    const revenueCol = sheet.getRangeByIndexes(dataStartRow + 1, revenueIndex, data.length, 1);
    revenueCol.numberFormat = Array(data.length).fill(["@"]);
  }

  // Write data rows
  if (data.length > 0) {
    const dataRange = sheet.getRangeByIndexes(dataStartRow + 1, 0, data.length, headers.length);
    dataRange.values = data;
  }

  // Auto-fit columns
  const fullRange = sheet.getRangeByIndexes(dataStartRow, 0, data.length + 1, headers.length);
  fullRange.format.autofitColumns();

  // Cap target_asin column width — ASINs are short codes, autofit over-expands due to merged title row
  const targetAsinIndex = headers.indexOf("target_asin");
  if (targetAsinIndex >= 0) {
    sheet.getRangeByIndexes(dataStartRow, targetAsinIndex, data.length + 1, 1).format.columnWidth = 110;
  }

  // Enable wrap text on the reasoning column and cap its width
  const reasoningIndex = headers.indexOf("reasoning");
  if (reasoningIndex >= 0 && data.length > 0) {
    const reasoningCol = sheet.getRangeByIndexes(dataStartRow + 1, reasoningIndex, data.length, 1);
    reasoningCol.format.wrapText = true;
    reasoningCol.format.columnWidth = 600;
    reasoningCol.format.autofitRows();
  }

  // Create table for easy filtering/sorting
  const tableRange = sheet.getRangeByIndexes(dataStartRow, 0, data.length + 1, headers.length);
  const table = sheet.tables.add(tableRange, true);
  table.name = `MMFlattened_${Date.now()}`;
  table.style = "TableStyleMedium2";

  // Auto-filter: show only aggregate summary rows by default
  const searchTermIndex = headers.indexOf("search_term");
  if (searchTermIndex >= 0) {
    const searchTermColumn = table.columns.getItemAt(searchTermIndex);
    searchTermColumn.filter.applyValuesFilter(["AGGREGATE"]);
  }

  // Auto-filter: hide rows where opportunity_revenue is blank.
  // `applyCustomFilter("<>")` is Excel's "not equal to empty" pattern — it
  // hides cells whose value is "" (the data writer above maps missing values
  // to "" via `row[h] ?? ""`). The column is text-formatted (currency strings
  // come pre-formatted from the backend); "<>" matches text-mode empty cells
  // correctly. Guarded with the revenueIndex check because the column is
  // conditionally present in the grouped grid headers.
  const opportunityRevenueIndex = headers.indexOf("opportunity_revenue");
  if (opportunityRevenueIndex >= 0) {
    const revenueColumn = table.columns.getItemAt(opportunityRevenueIndex);
    revenueColumn.filter.applyCustomFilter("<>");
  }

  

  // Apply strategy-based row highlighting
  let parentLevel3Color: string | null = null;

  for (let i = 0; i < groupedGrid.length; i++) {
    const row = groupedGrid[i];
    const level = Number(row._level ?? 0);
    const rank = Number(row.strategy_rank);
    const strategy = String(row.supports_strategy || "");

    let fillColor: string | null = null;

    if (level === 4) {
      if (parentLevel3Color) {
        fillColor = parentLevel3Color;
      } else if (strategy !== "NONE" && strategy !== "") {
        fillColor = STRATEGY_HIGHLIGHT.qualifying;
      }
    } else {
      if (level < 3) {
        parentLevel3Color = null;
      }
      if (rank === 1) {
        fillColor = STRATEGY_HIGHLIGHT.rank1;
      } else if (rank === 2) {
        fillColor = STRATEGY_HIGHLIGHT.rank2;
      } else if (rank === 0 && strategy !== "NONE" && strategy !== "") {
        fillColor = STRATEGY_HIGHLIGHT.qualifying;
      }

      if (level === 3) {
        parentLevel3Color = (rank === 1 || rank === 2) ? fillColor : null;
      }
    }

    if (fillColor) {
      const rowRange = sheet.getRangeByIndexes(dataStartRow + 1 + i, 0, 1, headers.length);
      rowRange.format.fill.color = fillColor;
    }
  }

  const priorityScoreIndex = headers.indexOf("priority_score");
  if (priorityScoreIndex >= 0) {
    table.sort.apply([{ key: priorityScoreIndex, ascending: false }]);
  }

  await context.sync();

  return sheet;
};

// Helper: Create executive dashboard header
export const createDashboardHeader = async (
  sheet: Excel.Worksheet,
  summary: string,
  appliedFilters: string,
  datePeriod?: { startDate?: string; endDate?: string }
): Promise<number> => {
  let nextRow = 4;
  // Row 1: Report title with branding (green theme)
  const titleRange = sheet.getRange("A1:L1");
  titleRange.merge();
  sheet.getRange("A1").values = [["📊 MARKET WATCH ANALYSIS REPORT"]];
  titleRange.format.font.size = 20;
  titleRange.format.font.bold = true;
  titleRange.format.font.color = "#000000";
  titleRange.format.fill.color = THEME.headerBg;
  titleRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
  titleRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  sheet.getRange("A1").format.rowHeight = 40;

  // Row 2: Applied filters (or "All Data" if no filters)
  const filterRange = sheet.getRange("A2:L2");
  filterRange.merge();
  const filterText = appliedFilters || "Showing: All Data (No Filters Applied)";
  sheet.getRange("A2").values = [[filterText]];
  filterRange.format.font.size = 12;
  filterRange.format.font.bold = true;
  filterRange.format.font.color = "#000000";
  filterRange.format.fill.color = THEME.primaryLight;
  filterRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
  sheet.getRange("A2").format.rowHeight = 24;

  // Row 3: Data period label
  const periodLabel = formatDateRangeLabel(datePeriod?.startDate, datePeriod?.endDate);
  const periodRange = sheet.getRange("A3:L3");
  periodRange.merge();
  sheet.getRange("A3").values = [[periodLabel]];
  periodRange.format.font.size = 10;
  periodRange.format.font.italic = true;
  periodRange.format.font.color = "#6B7280";
  periodRange.format.fill.color = THEME.primaryLight;
  periodRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
  sheet.getRange("A3").format.rowHeight = 22;

  // Row 4-5+: Executive summary box
  if (summary) {
    const summaryLabelRange = sheet.getRange("A4:L4");
    summaryLabelRange.merge();
    sheet.getRange("A4").values = [["EXECUTIVE SUMMARY"]];
    summaryLabelRange.format.font.size = 12;
    summaryLabelRange.format.font.bold = true;
    summaryLabelRange.format.font.color = "#000000";
    summaryLabelRange.format.fill.color = THEME.sectionBg;
    summaryLabelRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    sheet.getRange("A4").format.rowHeight = 24;

    const charsPerLine = 120;
    const estimatedLines = Math.ceil(summary.length / charsPerLine);
    const summaryRows = Math.min(6, Math.max(2, estimatedLines));
    const summaryEndRow = 4 + summaryRows;
    const summaryRange = sheet.getRange(`A5:L${summaryEndRow}`);
    summaryRange.merge();
    sheet.getRange("A5").values = [[summary]];
    sheet.getRange("A5").format.font.size = 12;
    sheet.getRange("A5").format.font.color = "#000000";
    sheet.getRange("A5").format.wrapText = true;
    sheet.getRange("A5").format.verticalAlignment = Excel.VerticalAlignment.top;
    sheet.getRange("A5").format.fill.color = THEME.cardBg;
    for (let r = 5; r <= summaryEndRow; r++) {
      sheet.getRange(`A${r}`).format.rowHeight = 20;
    }

    // Add border to summary box
    const fullSummaryBox = sheet.getRange(`A4:L${summaryEndRow}`);
    fullSummaryBox.format.borders.getItem("EdgeLeft").style = Excel.BorderLineStyle.continuous;
    fullSummaryBox.format.borders.getItem("EdgeRight").style = Excel.BorderLineStyle.continuous;
    fullSummaryBox.format.borders.getItem("EdgeTop").style = Excel.BorderLineStyle.continuous;
    fullSummaryBox.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
    fullSummaryBox.format.borders.getItem("EdgeLeft").color = THEME.cardBorder;
    fullSummaryBox.format.borders.getItem("EdgeRight").color = THEME.cardBorder;
    fullSummaryBox.format.borders.getItem("EdgeTop").color = THEME.cardBorder;
    fullSummaryBox.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;

    nextRow = summaryEndRow + 2;
  }

  return nextRow; // Next row after header section
};

// Helper: Create dynamic KPI dashboard section
export const createKPIDashboard = async (
  sheet: Excel.Worksheet,
  kpis: DynamicKPI[],
  startRow: number
): Promise<number> => {
  if (kpis.length === 0) return startRow;

  // Section header (green theme)
  const headerRange = sheet.getRange(`A${startRow}:L${startRow}`);
  headerRange.merge();
  sheet.getRange(`A${startRow}`).values = [["📈 KEY PERFORMANCE INDICATORS"]];
  headerRange.format.font.size = 13;
  headerRange.format.font.bold = true;
  headerRange.format.font.color = "#000000";
  headerRange.format.fill.color = THEME.sectionBg;
  sheet.getRange(`A${startRow}`).format.rowHeight = 26;
  startRow++;

  // Calculate grid layout (3 KPIs per row, 2 rows each KPI)
  const kpisPerRow = 3;
  const colStarts = ["A", "E", "I"];
  const colEnds = ["D", "H", "L"];

  for (let i = 0; i < kpis.length; i++) {
    const kpi = kpis[i];
    const rowInGrid = Math.floor(i / kpisPerRow);
    const colInGrid = i % kpisPerRow;

    const labelRow = startRow + rowInGrid * 2;
    const valueRow = labelRow + 1;
    const colStart = colStarts[colInGrid];
    const colEnd = colEnds[colInGrid];

    // Set row heights (only once per row)
    if (colInGrid === 0) {
      sheet.getRange(`A${labelRow}`).format.rowHeight = 20;
      sheet.getRange(`A${valueRow}`).format.rowHeight = 36;
    }

    // Label row
    const labelRange = sheet.getRange(`${colStart}${labelRow}:${colEnd}${labelRow}`);
    labelRange.merge();
    sheet.getRange(`${colStart}${labelRow}`).values = [[kpi.label]];
    labelRange.format.fill.color = kpi.colorTheme.header;
    labelRange.format.font.color = "#000000";
    labelRange.format.font.size = 10;
    labelRange.format.font.bold = true;
    labelRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;

    // Value row
    const valueRange = sheet.getRange(`${colStart}${valueRow}:${colEnd}${valueRow}`);
    valueRange.merge();
    // Set format and value on the top-left cell only (correct pattern for merged ranges)
    const valueCell = sheet.getRange(`${colStart}${valueRow}`);
    valueCell.numberFormat = [["@"]]; // "@" means text format
    valueCell.values = [[String(kpi.value)]];
    valueRange.format.fill.color = kpi.colorTheme.body;
    valueRange.format.font.color = "#000000";
    valueRange.format.font.size = 22;
    valueRange.format.font.bold = true;
    valueRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
    valueRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  }

  // Calculate rows used (2 rows per KPI row)
  const totalKpiRows = Math.ceil(kpis.length / kpisPerRow) * 2;
  return startRow + totalKpiRows + 1; // +1 for spacer
};

// Helper: Create dynamic data tables based on charts
export const createDataTables = async (
  sheet: Excel.Worksheet,
  charts: DynamicChart[],
  startRow: number
): Promise<number> => {
  if (charts.length === 0) return startRow;

  // Table positions: left (A:F) and right (G:L)
  const tableConfigs = [
    { colStart: "A", colMid: "B", colMidEnd: "D", colVal: "E", colValEnd: "F", headerCol: "A:F" },
    { colStart: "G", colMid: "H", colMidEnd: "J", colVal: "K", colValEnd: "L", headerCol: "G:L" },
  ];

  const icons = ["🔑", "🏪", "📊", "📈"];
  let maxRows = 0;

  for (let i = 0; i < Math.min(charts.length, 2); i++) {
    const chart = charts[i];
    const config = tableConfigs[i];
    let tableRow = startRow;

    // Section header (green theme)
    const headerRange = sheet.getRange(
      `${config.colStart}${tableRow}:${config.colValEnd}${tableRow}`
    );
    headerRange.merge();
    sheet.getRange(`${config.colStart}${tableRow}`).values = [
      [`${icons[i]} ${chart.title.toUpperCase()}`],
    ];
    headerRange.format.font.size = 13;
    headerRange.format.font.bold = true;
    headerRange.format.font.color = "#000000";
    headerRange.format.fill.color = THEME.sectionBg;
    if (i === 0) sheet.getRange(`${config.colStart}${tableRow}`).format.rowHeight = 26;
    tableRow++;

    // Table headers (green theme)
    sheet.getRange(`${config.colStart}${tableRow}`).values = [["#"]];
    sheet.getRange(`${config.colMid}${tableRow}:${config.colMidEnd}${tableRow}`).merge();
    sheet.getRange(`${config.colMid}${tableRow}`).values = [["Name"]];
    sheet.getRange(`${config.colVal}${tableRow}:${config.colValEnd}${tableRow}`).merge();
    sheet.getRange(`${config.colVal}${tableRow}`).values = [["Value"]];

    const tableHeaderRange = sheet.getRange(
      `${config.colStart}${tableRow}:${config.colValEnd}${tableRow}`
    );
    tableHeaderRange.format.fill.color = THEME.primary;
    tableHeaderRange.format.font.color = "#000000";
    tableHeaderRange.format.font.bold = true;
    tableHeaderRange.format.font.size = 12;
    tableHeaderRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
    if (i === 0) sheet.getRange(`${config.colStart}${tableRow}`).format.rowHeight = 24;
    tableRow++;

    // Data rows
    chart.data.slice(0, 5).forEach((item, idx) => {
      const rowNum = tableRow + idx;
      sheet.getRange(`${config.colStart}${rowNum}`).values = [[idx + 1]];
      sheet.getRange(`${config.colMid}${rowNum}:${config.colMidEnd}${rowNum}`).merge();
      sheet.getRange(`${config.colMid}${rowNum}`).values = [[item.label]];
      sheet.getRange(`${config.colVal}${rowNum}:${config.colValEnd}${rowNum}`).merge();

      // Format value based on chart type
      let displayValue: string;
      if (chart.type === "doughnut" || chart.type === "pie") {
        displayValue = `${item.value.toFixed(1)}%`;
      } else if (item.value >= 1000) {
        displayValue = item.value.toLocaleString();
      } else {
        displayValue = item.value.toFixed(item.value % 1 === 0 ? 0 : 1);
      }
      sheet.getRange(`${config.colVal}${rowNum}`).values = [[displayValue]];

      // Alternating row colors
      const rowRange = sheet.getRange(`${config.colStart}${rowNum}:${config.colValEnd}${rowNum}`);
      rowRange.format.fill.color = idx % 2 === 0 ? "#F8FAFC" : "#FFFFFF";
      rowRange.format.font.size = 11;
      rowRange.format.font.color = "#000000";
      if (i === 0) sheet.getRange(`${config.colStart}${rowNum}`).format.rowHeight = 22;

      // Highlight top item (green theme)
      if (idx === 0) {
        rowRange.format.font.bold = true;
        rowRange.format.fill.color = THEME.primaryLight;
      }

      // Alignment
      sheet.getRange(`${config.colStart}${rowNum}`).format.horizontalAlignment =
        Excel.HorizontalAlignment.center;
      sheet.getRange(`${config.colVal}${rowNum}`).format.horizontalAlignment =
        Excel.HorizontalAlignment.right;
    });

    const dataRowCount = Math.min(chart.data.length, 5);
    maxRows = Math.max(maxRows, dataRowCount);

    // Add border to table (green theme)
    if (dataRowCount > 0) {
      const tableRange = sheet.getRange(
        `${config.colStart}${startRow + 1}:${config.colValEnd}${tableRow + dataRowCount - 1}`
      );
      tableRange.format.borders.getItem("EdgeLeft").style = Excel.BorderLineStyle.continuous;
      tableRange.format.borders.getItem("EdgeRight").style = Excel.BorderLineStyle.continuous;
      tableRange.format.borders.getItem("EdgeTop").style = Excel.BorderLineStyle.continuous;
      tableRange.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
      tableRange.format.borders.getItem("EdgeLeft").color = THEME.cardBorder;
      tableRange.format.borders.getItem("EdgeRight").color = THEME.cardBorder;
      tableRange.format.borders.getItem("EdgeTop").color = THEME.cardBorder;
      tableRange.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;
    }
  }

  return startRow + maxRows + 3; // header + table header + data rows + spacer
};

// Helper: Create dynamic charts with professional styling
export const createDynamicExcelCharts = async (
  sheet: Excel.Worksheet,
  charts: DynamicChart[],
  context: Excel.RequestContext,
  startRow: number
): Promise<number> => {
  if (charts.length === 0) return startRow;

  // Section header (green theme)
  const headerRange = sheet.getRange(`A${startRow}:L${startRow}`);
  headerRange.merge();
  sheet.getRange(`A${startRow}`).values = [["📊 VISUAL ANALYTICS"]];
  headerRange.format.font.size = 13;
  headerRange.format.font.bold = true;
  headerRange.format.font.color = "#000000";
  headerRange.format.fill.color = THEME.sectionBg;
  sheet.getRange(`A${startRow}`).format.rowHeight = 26;
  startRow++;

  // Write chart data to hidden columns and create charts
  // Use column pairs: (AA, AB), (AD, AE) for chart data
  const dataColumnPairs = [
    { col1: "AA", col2: "AB" },
    { col1: "AD", col2: "AE" },
  ];
  const chartPositions = [
    { start: "A", end: "F" }, // Left chart
    { start: "G", end: "L" }, // Right chart
  ];

  // Set up chart area with proper row heights
  const chartEndRow = startRow + 14;
  for (let r = startRow; r <= chartEndRow; r++) {
    sheet.getRange(`A${r}`).format.rowHeight = 18;
  }
  await context.sync();

  for (let i = 0; i < Math.min(charts.length, 2); i++) {
    const chart = charts[i];

    // Skip charts with no data - prevents invalid range errors
    if (!chart.data || chart.data.length === 0) {
      console.warn(`Skipping chart "${chart.title}" - no data available`);
      continue;
    }

    const { col1: dataCol, col2: dataCol2 } = dataColumnPairs[i];
    const position = chartPositions[i];

    // Write data to hidden columns
    sheet.getRange(`${dataCol}1:${dataCol2}1`).values = [["Label", "Value"]];
    const chartData = chart.data.map((d) => [
      d.label.length > 20 ? d.label.substring(0, 20) + "..." : d.label,
      d.value,
    ]);
    sheet.getRange(`${dataCol}2:${dataCol2}${chart.data.length + 1}`).values = chartData;

    await context.sync();

    // Determine Excel chart type
    let excelChartType: Excel.ChartType;
    switch (chart.type) {
      case "bar":
        excelChartType = Excel.ChartType.barClustered;
        break;
      case "column":
        excelChartType = Excel.ChartType.columnClustered;
        break;
      case "pie":
        excelChartType = Excel.ChartType.pie;
        break;
      case "doughnut":
        excelChartType = Excel.ChartType.doughnut;
        break;
      case "line":
        excelChartType = Excel.ChartType.line;
        break;
      default:
        excelChartType = Excel.ChartType.columnClustered;
    }

    // Create chart
    try {
      const dataRange = sheet.getRange(`${dataCol}1:${dataCol2}${chart.data.length + 1}`);
      const excelChart = sheet.charts.add(excelChartType, dataRange, Excel.ChartSeriesBy.columns);

      excelChart.setPosition(`${position.start}${startRow}`, `${position.end}${chartEndRow}`);
      excelChart.title.text = chart.title;
      excelChart.title.format.font.size = 12;
      excelChart.title.format.font.bold = true;
      excelChart.title.format.font.color = "#000000";

      // Configure legend based on chart type
      if (chart.type === "pie" || chart.type === "doughnut") {
        excelChart.legend.visible = true;
        excelChart.legend.position = Excel.ChartLegendPosition.bottom;
      } else {
        excelChart.legend.visible = false;

        // Add axis titles for bar/column charts
        // Parse title to extract metric and category (format: "Metric by Category")
        const titleParts = chart.title.split(" by ");
        const metricName = titleParts[0] || "Value";
        const categoryName = titleParts[1] || "Category";

        // For bar charts: X-axis is value, Y-axis is category
        // For column charts: X-axis is category, Y-axis is value
        try {
          if (chart.type === "bar") {
            // Bar chart: horizontal bars
            // Category axis (vertical/Y) shows categories
            const categoryAxis = excelChart.axes.getItem(Excel.ChartAxisType.category);
            categoryAxis.title.text = categoryName;
            categoryAxis.title.visible = true;
            categoryAxis.title.format.font.size = 10;
            categoryAxis.title.format.font.bold = false;
            categoryAxis.title.format.font.color = "#000000";

            // Value axis (horizontal/X) shows values
            const valueAxis = excelChart.axes.getItem(Excel.ChartAxisType.value);
            valueAxis.title.text = metricName;
            valueAxis.title.visible = true;
            valueAxis.title.format.font.size = 10;
            valueAxis.title.format.font.bold = false;
            valueAxis.title.format.font.color = "#000000";
          } else if (chart.type === "column" || chart.type === "line") {
            // Column chart: vertical bars
            // Category axis (horizontal/X) shows categories
            const categoryAxis = excelChart.axes.getItem(Excel.ChartAxisType.category);
            categoryAxis.title.text = categoryName;
            categoryAxis.title.visible = true;
            categoryAxis.title.format.font.size = 10;
            categoryAxis.title.format.font.bold = false;
            categoryAxis.title.format.font.color = "#000000";

            // Value axis (vertical/Y) shows values
            const valueAxis = excelChart.axes.getItem(Excel.ChartAxisType.value);
            valueAxis.title.text = metricName;
            valueAxis.title.visible = true;
            valueAxis.title.format.font.size = 10;
            valueAxis.title.format.font.bold = false;
            valueAxis.title.format.font.color = "#000000";
          }
        } catch (axisErr) {
          console.warn("Could not set axis titles:", axisErr);
        }
      }

      // Style the chart (green theme)
      excelChart.format.fill.setSolidColor(THEME.cardBg);
      excelChart.format.border.lineStyle = Excel.ChartLineStyle.continuous;
      excelChart.format.border.color = THEME.cardBorder;

      await context.sync();
      console.log(`${chart.type} chart "${chart.title}" created successfully`);
    } catch (chartErr) {
      console.error(`Failed to create ${chart.type} chart:`, chartErr);
    }
  }

  return chartEndRow + 2; // Return next row after charts + spacer
};

// Helper: Create insights section from HTML report
export const createInsightsSection = async (
  sheet: Excel.Worksheet,
  htmlReport: string,
  startRow: number
): Promise<number> => {
  // === DEBUG: Log parsed sections ===
  console.log("=== INSIGHTS SECTION PARSING DEBUG ===");
  console.log("Raw HTML length:", htmlReport?.length);

  const sections = parseHtmlIntoSectionsDetailed(htmlReport);

  console.log("Parsed sections count:", sections.length);
  sections.forEach((section, idx) => {
    console.log(`--- Section ${idx + 1}: ${section.title} ---`);
    console.log("  Paragraphs:", section.paragraphs.length, section.paragraphs);
    console.log("  List items:", section.listItems.length, section.listItems);
    console.log("  Metrics:", section.metrics.length, section.metrics);
  });
  console.log("=== END PARSING DEBUG ===");

  if (sections.length === 0) {
    return startRow;
  }

  const getBadgeFill = (type: HtmlBadgeType | null): string | null => {
    switch (type) {
      case "critical":
        return "#FEE2E2";
      case "warning":
        return "#FEF3C7";
      case "info":
        return "#DBEAFE";
      case "good":
        return "#DCFCE7";
      default:
        return null;
    }
  };

  // Main section header (green theme)
  const mainHeaderRange = sheet.getRange(`A${startRow}:L${startRow}`);
  mainHeaderRange.merge();
  sheet.getRange(`A${startRow}`).values = [["📋 AI-POWERED INSIGHTS & RECOMMENDATIONS"]];
  mainHeaderRange.format.font.size = 14;
  mainHeaderRange.format.font.bold = true;
  mainHeaderRange.format.font.color = "#000000";
  mainHeaderRange.format.fill.color = THEME.sectionBg;
  sheet.getRange(`A${startRow}`).format.rowHeight = 28;
  startRow++;

  for (const section of sections) {
    const hasContent =
      section.paragraphs.length > 0 || section.listItems.length > 0 || section.metrics.length > 0;
    if (!hasContent) continue;

    // Section title with icon based on content
    let icon = "▸";
    const titleLower = section.title.toLowerCase();
    if (titleLower.includes("finding") || titleLower.includes("insight")) icon = "💡";
    else if (titleLower.includes("recommend") || titleLower.includes("action")) icon = "✅";
    else if (titleLower.includes("keyword")) icon = "🔑";
    else if (titleLower.includes("asin") || titleLower.includes("product")) icon = "📦";
    else if (titleLower.includes("competitor") || titleLower.includes("market")) icon = "🏪";
    else if (titleLower.includes("demand") || titleLower.includes("trend")) icon = "📈";
    else if (titleLower.includes("summary") || titleLower.includes("overview")) icon = "📊";
    const isSummarySection = titleLower.includes("summary") || titleLower.includes("overview");
    const isActionSection = titleLower.includes("recommend") || titleLower.includes("action");

    // Section title (green theme)
    const sectionTitleRange = sheet.getRange(`A${startRow}:L${startRow}`);
    sectionTitleRange.merge();
    sheet.getRange(`A${startRow}`).values = [[`${icon} ${section.title}`]];
    sheet.getRange(`A${startRow}`).format.font.bold = true;
    sheet.getRange(`A${startRow}`).format.font.size = 13;
    sheet.getRange(`A${startRow}`).format.font.color = "#000000";
    sheet.getRange(`A${startRow}`).format.fill.color = THEME.sectionBg;
    sheet.getRange(`A${startRow}`).format.rowHeight = 24;
    sectionTitleRange.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
    sectionTitleRange.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;
    startRow++;

    // Summary callout (first paragraph for summary sections)
    const paragraphs =
      isSummarySection && section.paragraphs.length > 0
        ? section.paragraphs.slice(1)
        : section.paragraphs;
    if (isSummarySection && section.paragraphs.length > 0) {
      const summaryRange = sheet.getRange(`A${startRow}:L${startRow}`);
      summaryRange.merge();
      sheet.getRange(`A${startRow}`).values = [[`Summary: ${section.paragraphs[0]}`]];
      sheet.getRange(`A${startRow}`).format.wrapText = true;
      sheet.getRange(`A${startRow}`).format.font.color = "#000000";
      sheet.getRange(`A${startRow}`).format.font.size = 12;
      sheet.getRange(`A${startRow}`).format.verticalAlignment = Excel.VerticalAlignment.top;
      sheet.getRange(`A${startRow}`).format.fill.color = THEME.cardBg;
      summaryRange.format.borders.getItem("EdgeLeft").style = Excel.BorderLineStyle.continuous;
      summaryRange.format.borders.getItem("EdgeRight").style = Excel.BorderLineStyle.continuous;
      summaryRange.format.borders.getItem("EdgeTop").style = Excel.BorderLineStyle.continuous;
      summaryRange.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
      summaryRange.format.borders.getItem("EdgeLeft").color = THEME.cardBorder;
      summaryRange.format.borders.getItem("EdgeRight").color = THEME.cardBorder;
      summaryRange.format.borders.getItem("EdgeTop").color = THEME.cardBorder;
      summaryRange.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;
      const charsPerLine = 110;
      const estimatedLines = Math.ceil(section.paragraphs[0].length / charsPerLine);
      const rowHeight = Math.max(26, Math.min(estimatedLines * 16, 200));
      sheet.getRange(`A${startRow}`).format.rowHeight = rowHeight;
      startRow++;
      // Spacer row to avoid collision with next header
      sheet.getRange(`A${startRow}:L${startRow}`).merge();
      sheet.getRange(`A${startRow}`).format.rowHeight = 6;
      startRow++;
    }

    // Metrics (if present)
    if (section.metrics.length > 0) {
      // Check if this is a recommendation/action section with a single call-to-action metric
      const isCallToAction =
        isActionSection && section.metrics.length === 1 && !section.metrics[0].label;

      if (isCallToAction) {
        // Render as prominent full-width call-to-action
        const metric = section.metrics[0];
        const ctaRange = sheet.getRange(`A${startRow}:L${startRow}`);
        ctaRange.merge();
        sheet.getRange(`A${startRow}`).values = [[metric.value || ""]];
        ctaRange.format.font.size = 18;
        ctaRange.format.font.bold = true;
        ctaRange.format.font.color = "#FFFFFF";
        ctaRange.format.fill.color = THEME.primary;
        ctaRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
        ctaRange.format.verticalAlignment = Excel.VerticalAlignment.center;
        sheet.getRange(`A${startRow}`).format.rowHeight = 40;
        ctaRange.format.borders.getItem("EdgeLeft").style = Excel.BorderLineStyle.continuous;
        ctaRange.format.borders.getItem("EdgeRight").style = Excel.BorderLineStyle.continuous;
        ctaRange.format.borders.getItem("EdgeTop").style = Excel.BorderLineStyle.continuous;
        ctaRange.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
        ctaRange.format.borders.getItem("EdgeLeft").color = THEME.cardBorder;
        ctaRange.format.borders.getItem("EdgeRight").color = THEME.cardBorder;
        ctaRange.format.borders.getItem("EdgeTop").color = THEME.cardBorder;
        ctaRange.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;
        startRow++;
      } else {
        // Standard metrics grid
        const metricsHeader = sheet.getRange(`A${startRow}:L${startRow}`);
        metricsHeader.merge();
        sheet.getRange(`A${startRow}`).values = [["Key Metrics"]];
        sheet.getRange(`A${startRow}`).format.font.bold = true;
        sheet.getRange(`A${startRow}`).format.font.size = 12;
        sheet.getRange(`A${startRow}`).format.font.color = "#000000";
        sheet.getRange(`A${startRow}`).format.fill.color = THEME.cardBg;
        sheet.getRange(`A${startRow}`).format.rowHeight = 22;
        startRow++;

        const metricsPerRow = 2;
        const leftCols = {
          valueStart: "A",
          valueEnd: "D",
          badgeStart: "E",
          badgeEnd: "F",
          labelStart: "A",
          labelEnd: "F",
          boxStart: "A",
          boxEnd: "F",
        };
        const rightCols = {
          valueStart: "G",
          valueEnd: "J",
          badgeStart: "K",
          badgeEnd: "L",
          labelStart: "G",
          labelEnd: "L",
          boxStart: "G",
          boxEnd: "L",
        };

        for (let i = 0; i < section.metrics.length; i++) {
          const metric = section.metrics[i];
          const rowInGrid = Math.floor(i / metricsPerRow);
          const colInGrid = i % metricsPerRow;
          const cardRow = startRow + rowInGrid * 3;

          if (colInGrid === 0) {
            sheet.getRange(`A${cardRow}`).format.rowHeight = 24;
            sheet.getRange(`A${cardRow + 1}`).format.rowHeight = 20;
            sheet.getRange(`A${cardRow + 2}`).format.rowHeight = 6;
          }

          const cols = colInGrid === 0 ? leftCols : rightCols;

          const valueRange = sheet.getRange(
            `${cols.valueStart}${cardRow}:${cols.valueEnd}${cardRow}`
          );
          valueRange.merge();
          sheet.getRange(`${cols.valueStart}${cardRow}`).values = [[metric.value || ""]];
          valueRange.format.font.size = 15;
          valueRange.format.font.bold = true;
          valueRange.format.font.color = "#000000";
          valueRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;

          const badgeRange = sheet.getRange(
            `${cols.badgeStart}${cardRow}:${cols.badgeEnd}${cardRow}`
          );
          badgeRange.merge();
          sheet.getRange(`${cols.badgeStart}${cardRow}`).values = [[metric.badge?.text || ""]];
          badgeRange.format.font.size = 10;
          badgeRange.format.font.bold = true;
          badgeRange.format.font.color = "#000000";
          badgeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
          const badgeFill = getBadgeFill(metric.badge?.type || null);
          if (badgeFill) {
            badgeRange.format.fill.color = badgeFill;
          }

          const labelRange = sheet.getRange(
            `${cols.labelStart}${cardRow + 1}:${cols.labelEnd}${cardRow + 1}`
          );
          labelRange.merge();
          sheet.getRange(`${cols.labelStart}${cardRow + 1}`).values = [[metric.label || ""]];
          labelRange.format.font.size = 11;
          labelRange.format.font.color = "#000000";
          labelRange.format.wrapText = true;

          const cardRange = sheet.getRange(
            `${cols.boxStart}${cardRow}:${cols.boxEnd}${cardRow + 1}`
          );
          cardRange.format.fill.color = "#F8FAFC";
          cardRange.format.borders.getItem("EdgeLeft").style = Excel.BorderLineStyle.continuous;
          cardRange.format.borders.getItem("EdgeRight").style = Excel.BorderLineStyle.continuous;
          cardRange.format.borders.getItem("EdgeTop").style = Excel.BorderLineStyle.continuous;
          cardRange.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
          cardRange.format.borders.getItem("EdgeLeft").color = THEME.cardBorder;
          cardRange.format.borders.getItem("EdgeRight").color = THEME.cardBorder;
          cardRange.format.borders.getItem("EdgeTop").color = THEME.cardBorder;
          cardRange.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;
        }

        const rowsUsed = Math.ceil(section.metrics.length / metricsPerRow) * 3;
        startRow += rowsUsed;
      }
    }

    // Paragraphs
    for (const paragraph of paragraphs) {
      if (!paragraph) continue;
      const contentRange = sheet.getRange(`A${startRow}:L${startRow}`);
      contentRange.merge();
      sheet.getRange(`A${startRow}`).values = [[paragraph]];
      sheet.getRange(`A${startRow}`).format.wrapText = true;
      sheet.getRange(`A${startRow}`).format.font.color = "#000000";
      sheet.getRange(`A${startRow}`).format.font.size = 12;
      sheet.getRange(`A${startRow}`).format.verticalAlignment = Excel.VerticalAlignment.top;
      sheet.getRange(`A${startRow}`).format.fill.color = THEME.cardBg;
      // Avoid indentLevel to prevent Excel API errors across clients

      const charsPerLine = 100;
      const estimatedLines = Math.ceil(paragraph.length / charsPerLine);
      const rowHeight = Math.max(25, Math.min(estimatedLines * 16, 200));
      sheet.getRange(`A${startRow}`).format.rowHeight = rowHeight;
      startRow++;
    }

    // List header (if list items exist)
    if (section.listItems.length > 0) {
      const listHeader = sheet.getRange(`A${startRow}:L${startRow}`);
      listHeader.merge();
      sheet.getRange(`A${startRow}`).values = [[isActionSection ? "Action Items" : "Key Findings"]];
      sheet.getRange(`A${startRow}`).format.font.bold = true;
      sheet.getRange(`A${startRow}`).format.font.size = 12;
      sheet.getRange(`A${startRow}`).format.font.color = "#000000";
      sheet.getRange(`A${startRow}`).format.fill.color = THEME.cardBg;
      sheet.getRange(`A${startRow}`).format.rowHeight = 22;
      startRow++;
    }

    // List items with badges
    for (let i = 0; i < section.listItems.length; i++) {
      const item = section.listItems[i];
      const textRange = sheet.getRange(`A${startRow}:J${startRow}`);
      textRange.merge();
      sheet.getRange(`A${startRow}`).values = [[`• ${item.text}`]];
      textRange.format.wrapText = true;
      textRange.format.font.size = 12;
      textRange.format.font.color = "#000000";

      const badgeRange = sheet.getRange(`K${startRow}:L${startRow}`);
      badgeRange.merge();
      sheet.getRange(`K${startRow}`).values = [[item.badge?.text || ""]];
      badgeRange.format.font.size = 10;
      badgeRange.format.font.bold = true;
      badgeRange.format.font.color = "#000000";
      badgeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      const badgeFill = getBadgeFill(item.badge?.type || null);
      if (badgeFill) {
        badgeRange.format.fill.color = badgeFill;
      }

      const rowRange = sheet.getRange(`A${startRow}:L${startRow}`);
      rowRange.format.fill.color = i % 2 === 0 ? "#FFFFFF" : "#F8FAFC";
      rowRange.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
      rowRange.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;

      const charsPerLine = 90;
      const estimatedLines = Math.ceil(item.text.length / charsPerLine);
      const rowHeight = Math.max(24, Math.min(estimatedLines * 16, 220));
      sheet.getRange(`A${startRow}`).format.rowHeight = rowHeight;
      startRow++;
    }

    // Add subtle border and spacing
    const contentBox = sheet.getRange(`A${startRow - 1}:L${startRow - 1}`);
    contentBox.format.borders.getItem("EdgeBottom").style = Excel.BorderLineStyle.continuous;
    contentBox.format.borders.getItem("EdgeBottom").color = THEME.cardBorder;
    startRow++;
  }

  return startRow;
};

// Helper: Create footer with metadata
export const createFooter = async (sheet: Excel.Worksheet, startRow: number): Promise<void> => {
  // Divider line (green theme)
  const dividerRange = sheet.getRange(`A${startRow}:L${startRow}`);
  dividerRange.merge();
  dividerRange.format.borders.getItem("EdgeTop").style = Excel.BorderLineStyle.continuous;
  dividerRange.format.borders.getItem("EdgeTop").color = THEME.cardBorder;
  sheet.getRange(`A${startRow}`).format.rowHeight = 10;
  startRow++;

  // Footer text (green theme)
  const footerRange = sheet.getRange(`A${startRow}:L${startRow}`);
  footerRange.merge();
  const footerText = `Generated by SmartGrid Market Watch | ${new Date().toLocaleString()} | Data Source: Market Watch Dataset`;
  sheet.getRange(`A${startRow}`).values = [[footerText]];
  sheet.getRange(`A${startRow}`).format.font.size = 10;
  sheet.getRange(`A${startRow}`).format.font.color = "#000000";
  sheet.getRange(`A${startRow}`).format.font.italic = true;
  sheet.getRange(`A${startRow}`).format.horizontalAlignment = Excel.HorizontalAlignment.center;
  sheet.getRange(`A${startRow}`).format.rowHeight = 20;
};

// Main: Create professional analysis report sheet with dynamic KPIs and Charts
export const createAnalysisSheet = async (
  context: Excel.RequestContext,
  htmlReport: string,
  summary: string,
  groupedGrid?: Array<Record<string, any>>,
  appliedFilters?: string,
  filterContext?: FilterContext,
  timestamp?: string,
  datePeriod?: { startDate?: string; endDate?: string }
) => {
  const ts = timestamp || generateTimestamp();
  const sheetName = `MW Recommendations ${ts}`;
  const sheet = context.workbook.worksheets.add(sheetName);

  // Set column widths for professional layout
  sheet.getRange("A:A").format.columnWidth = 66;
  sheet.getRange("B:B").format.columnWidth = 71;
  sheet.getRange("C:C").format.columnWidth = 71;
  sheet.getRange("D:D").format.columnWidth = 71;
  sheet.getRange("E:E").format.columnWidth = 66;
  sheet.getRange("F:F").format.columnWidth = 71;
  sheet.getRange("G:G").format.columnWidth = 66;
  sheet.getRange("H:H").format.columnWidth = 71;
  sheet.getRange("I:I").format.columnWidth = 71;
  sheet.getRange("J:J").format.columnWidth = 71;
  sheet.getRange("K:K").format.columnWidth = 66;
  sheet.getRange("L:L").format.columnWidth = 71;
  await context.sync();
  console.log("MM analysis: column widths set");

  // Generate dynamic KPIs and Charts based on flattened pivot data
  let dynamicKPIs: DynamicKPI[] = [];
  let dynamicCharts: DynamicChart[] = [];

  // Convert grouped_grid (array of objects) to EnrichedData format (headers + rows)
  // Keep _level column for hierarchy detection in KPI/chart generation
  if (groupedGrid && groupedGrid.length > 0) {
    const allHeaders = Object.keys(groupedGrid[0]);
    // Include _level for hierarchy detection, exclude other internal fields
    const headers = allHeaders.filter((k) => k === "_level" || !k.startsWith("_"));
    const rows = groupedGrid.map((row) => headers.map((h) => row[h] ?? ""));
    const enrichedData: EnrichedData = { headers, rows };

    // Detailed debug logging
    console.log("=== ANALYSIS SHEET DATA DEBUG ===");
    console.log("grouped_grid length:", groupedGrid.length);
    console.log("All headers from grouped_grid:", allHeaders);
    console.log("Filtered headers (with _level):", headers);
    console.log("Sample row 0:", groupedGrid[0]);
    console.log("Sample row 1:", groupedGrid[1]);
    console.log("Converted enrichedData:", { headers: headers.length, rows: rows.length });
    console.log("Sample converted row 0:", rows[0]);
    console.log("Sample converted row 1:", rows[1]);

    // Generate KPIs and Charts from flattened pivot data
    dynamicKPIs = generateDynamicKPIs(enrichedData, htmlReport || "", summary || "");
    dynamicCharts = generateDynamicCharts(enrichedData, htmlReport || "", summary || "");

    console.log("=== GENERATION RESULTS ===");
    console.log("KPIs generated:", dynamicKPIs.length);
    console.log(
      "KPI details:",
      dynamicKPIs.map((k) => ({ label: k.label, value: k.value }))
    );
    console.log("Charts generated:", dynamicCharts.length);
    console.log(
      "Chart details:",
      dynamicCharts.map((c) => ({ title: c.title, type: c.type, dataPoints: c.data.length }))
    );
    console.log("Filter context:", filterContext);
  } else {
    console.warn("=== NO GROUPED_GRID DATA ===");
    console.warn("groupedGrid is empty or undefined:", groupedGrid);
  }

  let currentRow = 1;
  try {
    // Section 1: Dashboard Header (rows 1-5)
    currentRow = await createDashboardHeader(sheet, summary, appliedFilters || "", datePeriod);
    await context.sync();
    console.log("MM analysis: header rendered");
  } catch (headerErr) {
    console.error("Failed to render dashboard header:", headerErr);
    currentRow = 7;
  }

  try {
    // Section 2: Dynamic KPI Dashboard
    if (dynamicKPIs.length > 0) {
      const kpiEndRow = await createKPIDashboard(sheet, dynamicKPIs, currentRow);
      await context.sync();
      currentRow = kpiEndRow;
      console.log("MM analysis: KPI dashboard rendered at row", currentRow);
    }
  } catch (kpiErr) {
    console.error("Failed to render KPI dashboard:", kpiErr);
    currentRow += 5; // Safe fallback spacing
  }

  // Spacer
  currentRow++;

  try {
    // Section 3: Dynamic Charts (simplified - no separate data tables)
    if (dynamicCharts.length > 0) {
      const chartEndRow = await createDynamicExcelCharts(sheet, dynamicCharts, context, currentRow);
      await context.sync();
      currentRow = chartEndRow;
      console.log("MM analysis: charts rendered at row", currentRow);
    }
  } catch (chartErr) {
    console.error("Failed to render charts:", chartErr);
    currentRow += 17; // Approximate chart height fallback
  }

  try {
    // Section 4: AI Insights from HTML report
    if (htmlReport) {
      currentRow = await createInsightsSection(sheet, htmlReport, currentRow);
      await context.sync();
      console.log("MM analysis: insights rendered");
    }
  } catch (insightsErr) {
    console.error("Failed to render insights section:", insightsErr);
  }

  try {
    // Footer
    await createFooter(sheet, currentRow);
    await context.sync();
    console.log("MM analysis: footer rendered");
  } catch (footerErr) {
    console.error("Failed to render footer:", footerErr);
  }

  await context.sync();

  return sheet;
};
